import { applySnapshot, cast, flow, getRoot, onSnapshot, types } from 'mobx-state-tree';
import { persist } from 'mst-persist';
import { AccountApi, CoreApi } from 'service/axios-api';
import { IRootStore } from './root';
import { MapleStoryProductId } from 'types/enum';

const {
    electron: { application },
} = window;

type StrToAnyMap = { [key: string]: any };

const ServerSideStore = types
    .model('ServerSession', {
        minimizePanel: types.optional(types.boolean, false),
    })
    .actions((self) => ({
        toggleMinimizePanel() {
            self.minimizePanel = !self.minimizePanel;
        },
    }));

// this will be stored in localStorage
export default types
    .model('Session', {
        autoLogin: types.optional(types.boolean, true),
        not_popup_ads: types.optional(types.boolean, false),
        dismissed_banners: types.array(types.number),
        isPreview: types.optional(types.boolean, false),
        rewardsHidden: types.optional(types.boolean, false),
        libraryOrder: types.array(types.string),
        lastPlayedProductId: types.optional(types.string, ''),
        flags: types.optional(types.map(types.boolean), {}),
        selectedServer: types.optional(types.string, 'North America'),
        serverSide: types.optional(ServerSideStore, {}),
    })
    .views((self) => ({
        get root(): IRootStore {
            return getRoot<IRootStore>(self);
        },
    }))
    .actions((self) => ({
        set({ not_popup_ads = self.not_popup_ads, autoLogin = self.autoLogin }: any) {
            self.not_popup_ads = not_popup_ads;
            self.autoLogin = autoLogin; // it needs to be synced with account auto login checkbox without below /login/launcher/cookie api call
        },
        async toggleAutoLogin() {
            self.autoLogin = !self.autoLogin;
            const { loginSessionExpiresIn }: { loginSessionExpiresIn: number } = await AccountApi.put('/login/launcher/cookie', {
                deviceId: application.params.device_id,
                autoLogin: self.autoLogin,
            });
            sessionStorage.setItem('refreshSessionAt', (Date.now() + loginSessionExpiresIn * 1000).toString());
        },
    }))
    .actions((self) => ({
        saveDismissedBanners(list: number[]) {
            // use Set in order to merge arrays without duplication
            const added = [...new Set([...self.dismissed_banners, ...list])];
            self.dismissed_banners = cast(added.filter((x) => self.root.promotions.banners.find((o) => o.id === x)));
        },
        updatePreviewMode(val: boolean) {
            self.isPreview = val;
        },
        setLibraryOrder(order: string[]) {
            self.libraryOrder = cast(order);
        },
        // last played game or shortcut
        setLastPlayedGame(productId: string) {
            self.lastPlayedProductId = productId;
        },
        setSelectedServer(server: string) {
            self.selectedServer = server;
        },
    }))
    .actions((self) => ({
        fetch: flow(function* (withServerSide: boolean = true) {
            // migration for old session
            if (localStorage.getItem('nxl-session') === null) {
                const oldValue = localStorage.getItem('session') || '{}';
                let json = JSON.parse(oldValue);
                delete json['flags'];
                localStorage.setItem('nxl-session', JSON.stringify(json));
                localStorage.removeItem('session');
            }
            ///

            yield persist('nxl-session', self, {
                storage: localStorage,
            });

            // server side data sync
            // it's only available after login
            if (window.win.name === 'main' && withServerSide) {
                const { data } = yield CoreApi.get('/user-data/v1/launcher');
                applySnapshot(self.serverSide, JSON.parse(data.launcherData || '{}'));

                onSnapshot(self.serverSide, (snapshot: StrToAnyMap) => {
                    CoreApi.put('/user-data/v1/launcher', { launcherData: JSON.stringify(snapshot) });
                });
            }

            // sync with other window (settings)
            window.addEventListener('storage', (e) => {
                if (e.key !== 'nxl-session' || !e.newValue) return;
                applySnapshot(self, JSON.parse(e.newValue));
            });

            if (application.params.environment === 'live') {
                self.updatePreviewMode(self.isPreview);
            }

            if (self.root.systemInfo.onMac) {
                // mac에서 실행되는 경우 메이플 스토리 패널만 노출
                self.lastPlayedProductId = MapleStoryProductId;
            }
        }),
    }));
